import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import moment from 'moment';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import ButtonBase from '@mui/material/ButtonBase';
import Paper from '@mui/material/Paper';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import RefreshIcon from '@mui/icons-material/Refresh';
import DownloadIcon from '@mui/icons-material/Download';
import ExitToAppIcon from '@mui/icons-material/ExitToApp';
import ChevronRight from '@mui/icons-material/ChevronRight';
import ChevronLeft from '@mui/icons-material/ChevronLeft';
import { useTheme } from '@mui/material/styles';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import { grey } from '@mui/material/colors';
import * as api from '../../api';
import { getScoresWithPoints, getFlagSrc } from '../../utils/scoring';

async function pMap(items, fn, concurrency = 5) {
  const results = new Array(items.length);
  let i = 0;
  async function worker() {
    while (i < items.length) {
      const idx = i++;
      results[idx] = await fn(items[idx], idx);
    }
  }
  await Promise.all(Array.from({ length: Math.min(concurrency, items.length) }, () => worker()));
  return results;
}

const CACHE_KEY = '__meprofile_cache_v2';

const ROLE_LABELS = { ro: 'RO', cro: 'CRO', so: 'SO', qm: 'QM', rm: 'RM', md: 'MD' };

function loadCache(publicId) {
  try {
    const raw = sessionStorage.getItem(CACHE_KEY);
    if (!raw) return null;
    const cached = JSON.parse(raw);
    if (cached.publicId !== publicId) return null;
    return cached;
  } catch { return null; }
}

function saveCache(publicId, data) {
  try {
    sessionStorage.setItem(CACHE_KEY, JSON.stringify({ publicId, ...data }));
  } catch { /* quota exceeded — ignore */ }
}

export default function MeProfile({ publicId, onLogout }) {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const theme = useTheme();
  const isRtl = theme.direction === 'rtl';
  const Chevron = isRtl ? ChevronLeft : ChevronRight;

  const cached = () => loadCache(publicId);
  const [registrations, setRegistrations] = useState(() => cached()?.registrations || null);
  const [staff, setStaff] = useState(() => cached()?.staff || null);
  const [matches, setMatches] = useState(() => cached()?.matches || null);
  const [stages, setStages] = useState(() => cached()?.stages || null);
  const [shooters, setShooters] = useState(() => cached()?.shooters || null);
  const [scores, setScores] = useState(() => cached()?.scores || null);
  const [progress, setProgress] = useState(null); // { step, done, total, pct }
  const [paymentRequired, setPaymentRequired] = useState(false);

  async function refresh() {
    setRegistrations(null);
    setStaff(null);
    setMatches(null);
    setStages(null);
    setShooters(null);
    setScores(null);

    setProgress({ step: 'finding', done: 0, total: 0, pct: 0 });
    let data;
    try {
      ({ data } = await api.getMatchesForShooter(publicId));
    } catch (err) {
      setProgress(null);
      if (err?.response?.status === 402) {
        setPaymentRequired(true);
        return;
      }
      throw err;
    }
    const regs = data.registrations || [];
    const staffList = data.staff || [];
    setRegistrations(regs);
    setStaff(staffList);

    const matchIds = [...new Set([...staffList.map((s) => s.matchId), ...regs.map((r) => r.matchId)])];
    const totalMatches = matchIds.length;
    let matchesDone = 0;
    setProgress({ step: 'matches', done: 0, total: totalMatches, pct: 10 });
    const matchResults = await pMap(matchIds, async (id) => {
      try {
        const res = await api.getMatch(id);
        return res;
      } catch { return null; }
      finally {
        matchesDone++;
        setProgress({ step: 'matches', done: matchesDone, total: totalMatches, pct: 10 + Math.round((matchesDone / totalMatches) * 30) });
      }
    });
    const matchList = matchResults.filter(Boolean).map((r) => r.data);
    matchList.sort((a, b) => {
      const da = new Date(a.startDate.year, a.startDate.month - 1, a.startDate.day);
      const db = new Date(b.startDate.year, b.startDate.month - 1, b.startDate.day);
      return db - da;
    });
    setMatches(matchList);

    const stageIds = matchList.flatMap((m) => m.stages || []).filter(Boolean);
    setProgress({ step: 'stages', done: 0, total: stageIds.length, pct: 45 });
    const allStages = [];
    for (let i = 0; i < stageIds.length; i += 50) {
      const chunk = stageIds.slice(i, i + 50);
      const res = await api.getStages(chunk.join(','));
      const stageData = res.data;
      allStages.push(...(Array.isArray(stageData) ? stageData : stageData ? [stageData] : []));
      setProgress({ step: 'stages', done: Math.min(i + 50, stageIds.length), total: stageIds.length, pct: 45 + Math.round((Math.min(i + 50, stageIds.length) / stageIds.length) * 15) });
    }

    const scoredMatches = matchList.filter((m) => m.scoresPublished);
    const totalScored = scoredMatches.length;
    let scoresDone = 0;
    setProgress({ step: 'scores', done: 0, total: totalScored, pct: 60 });
    const allScores = await pMap(matchList, async (m) => {
      if (!m.scoresPublished) return [];
      try {
        const r = await api.getScores(m.id);
        const d = r.data;
        return Array.isArray(d) ? d : d?.scores || [];
      } catch { return []; }
      finally {
        scoresDone++;
        setProgress({ step: 'scores', done: scoresDone, total: totalScored, pct: 60 + Math.round((scoresDone / Math.max(totalScored, 1)) * 20) });
      }
    });

    let shootersDone = 0;
    setProgress({ step: 'shooters', done: 0, total: totalScored, pct: 80 });
    const allShooters = await pMap(matchList, async (m) => {
      if (!m.scoresPublished) return [];
      try {
        const r = await api.getShooters(m.id);
        return r.data || [];
      } catch { return []; }
      finally {
        shootersDone++;
        setProgress({ step: 'shooters', done: shootersDone, total: totalScored, pct: 80 + Math.round((shootersDone / Math.max(totalScored, 1)) * 20) });
      }
    });

    setStages(allStages);
    setScores(allScores);
    setShooters(allShooters);
    saveCache(publicId, { registrations: regs, staff: staffList, matches: matchList, stages: allStages, scores: allScores, shooters: allShooters });
    setProgress(null);
  }

  useEffect(() => {
    if (publicId && !matches) refresh();
  }, [publicId]);

  const STEP_MESSAGES = {
    finding: [
      t('app.loadFinding1', 'Searching the universe for your matches...'),
      t('app.loadFinding2', 'Asking the range officer where you\'ve been...'),
    ],
    matches: [
      t('app.loadMatches1', 'Loading match details...'),
      t('app.loadMatches2', 'Unpacking match bags...'),
      t('app.loadMatches3', 'Checking if you remembered your belt...'),
    ],
    stages: [
      t('app.loadStages1', 'Loading stages...'),
      t('app.loadStages2', 'Taping targets...'),
      t('app.loadStages3', 'Resetting steel...'),
    ],
    scores: [
      t('app.loadScores1', 'Loading scores...'),
      t('app.loadScores2', 'Counting alphas (and trying to ignore the mikes)...'),
      t('app.loadScores3', 'Double-checking the math on your hit factor...'),
    ],
    shooters: [
      t('app.loadShooters1', 'Loading shooter data...'),
      t('app.loadShooters2', 'Rounding up the squad...'),
      t('app.loadShooters3', 'Almost there, make ready!'),
    ],
  };

  if (paymentRequired) {
    return (
      <Dialog open onClose={onLogout} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle>{t('app.mePaymentRequiredTitle')}</DialogTitle>
        <DialogContent>
          <Typography variant="body2">{t('app.mePaymentRequiredMessage')}</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={onLogout} variant="contained" sx={{ borderRadius: 2 }}>
            {t('common.close')}
          </Button>
        </DialogActions>
      </Dialog>
    );
  }

  if (progress || !matches) {
    const step = progress?.step || 'finding';
    const msgs = STEP_MESSAGES[step] || [];
    const msgIdx = progress?.total > 0
      ? Math.min(Math.floor((progress.done / progress.total) * msgs.length), msgs.length - 1)
      : 0;
    const message = msgs[msgIdx] || msgs[0] || '';
    const counter = progress?.total > 0 ? `\u2066${progress.done} / ${progress.total}\u2069` : '';
    const pct = progress?.pct || 0;

    return (
      <Box sx={{
        display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
        minHeight: 'calc(100vh - 56px)', bgcolor: '#f5f5f5', px: 3,
      }}>
        {/* Progress bar */}
        <Box sx={{ width: '100%', maxWidth: 280, mb: 3 }}>
          <Box sx={{
            height: 6, borderRadius: 3, bgcolor: 'rgba(25,118,210,0.12)', overflow: 'hidden',
          }}>
            <Box sx={{
              height: '100%', borderRadius: 3,
              bgcolor: 'primary.main',
              width: `${pct}%`,
              transition: 'width 0.3s ease',
            }} />
          </Box>
        </Box>

        <Typography variant="body2" sx={{ color: 'text.primary', fontWeight: 500, textAlign: 'center' }}>
          {message}
        </Typography>
        {counter && (
          <Typography variant="caption" sx={{ color: 'text.secondary', mt: 0.5, direction: 'ltr', unicodeBidi: 'bidi-override' }}>
            {counter}
          </Typography>
        )}
      </Box>
    );
  }

  function toDivisionKey(division) {
    const map = {
      'standard': 'standard', 'production': 'production', 'open': 'open',
      'production-optics': 'productionOptics', 'optics': 'optics', 'classic': 'classic',
      'revolver': 'revolver', 'pcc-optics': 'pccOptics', 'pcc-iron': 'pccIron',
      'sg-open': 'sgOpen', 'sg-modified': 'sgModified', 'sg-standard': 'sgStandard',
      'sg-standard-manual': 'sgStandardManual',
    };
    return map[division] || division;
  }

  function toCategoryKey(category) {
    const map = {
      'regular': 'regular', 'senior': 'senior', 'super-senior': 'superSenior',
      'grand-senior': 'grandSenior', 'lady-senior': 'ladySenior', 'junior': 'junior',
      'super-junior': 'superJunior', 'grand-junior': 'grandJunior', 'lady-junior': 'ladyJunior',
      'lady-super-junior': 'ladySuperJunior', 'lady-grand-junior': 'ladyGrandJunior', 'lady': 'lady',
    };
    return map[category] || category;
  }

  function getTrophyColor(pos) {
    if (pos === 1) return '#FFD700';
    if (pos === 2) return '#C0C0C0';
    if (pos === 3) return '#CD7F32';
    return null;
  }

  // A shooter belongs to the signed-in person if their publicId is the base id
  // (e.g. IL|30116) or an extra-division variant of it (IL|30116-1, -2, ...).
  function belongsToMe(pid) {
    return pid === publicId || (typeof pid === 'string' && pid.startsWith(`${publicId}-`));
  }

  // All registrations of the signed-in person within a single match. For matches
  // whose shooters were fetched (scores published) this surfaces every division
  // entry (base + -N variants); otherwise it falls back to the base registration.
  function myRegistrations(match, matchIdx) {
    const matchShooters = shooters?.[matchIdx];
    if (matchShooters && matchShooters.length) {
      const mine = matchShooters.filter((s) => belongsToMe(s.publicId));
      if (mine.length) return mine;
    }
    const reg = registrations.find((r) => r.matchId === match.id);
    return reg ? [reg] : [];
  }

  // Placement / percentage for one registration within its division, or { dq: true }.
  function computeResult(match, matchIdx, reg) {
    if (!(match.scoresPublished && reg && scores[matchIdx]?.length > 0)) return null;
    try {
      const matchStages = (match.stages || [])
        .map((sid) => stages.find((s) => s && s.id?.toString() === sid?.toString()))
        .filter(Boolean);
      const { scoresOverall, dqs } = getScoresWithPoints({
        scores: scores[matchIdx],
        stages: matchStages,
        shooters: shooters[matchIdx],
      });
      if (dqs.some((d) => d.shooterId === reg.id)) return { dq: true };
      const divScores = scoresOverall.filter((s) => s.division === reg.division);
      const pos = divScores.findIndex((s) => s.shooterId === reg.id);
      if (pos < 0) return null;
      const pct = divScores[pos].percentage != null
        ? divScores[pos].percentage
        : divScores.length > 0
          ? Math.round((divScores[pos].score / divScores[0].score) * 10000) / 100
          : 0;
      const result = { pos: pos + 1, total: divScores.length, percentage: (pct / 100).toFixed(2) };
      // Category placement within the division (e.g. Senior in Production), for non-regular categories.
      const category = divScores[pos].category;
      if (category && category !== 'regular') {
        const catScores = divScores.filter((s) => s.category === category);
        result.category = category;
        result.categoryPos = catScores.findIndex((s) => s.shooterId === reg.id) + 1;
        result.categoryTotal = catScores.length;
      }
      return result;
    } catch { return null; }
  }

  // Fraction of stages this registration has a score on (for the CSV export).
  function computeStagesPct(match, matchIdx, reg) {
    const matchStages = (match.stages || [])
      .map((sid) => stages.find((s) => s && s.id?.toString() === sid?.toString()))
      .filter(Boolean);
    if (!(match.scoresPublished && reg && scores[matchIdx]?.length > 0 && matchStages.length > 0)) return '';
    try {
      const { scoresByStage } = getScoresWithPoints({
        scores: scores[matchIdx],
        stages: matchStages,
        shooters: shooters[matchIdx],
      });
      const scoredStages = scoresByStage.filter((ss) => ss.some((s) => s.shooterId === reg.id)).length;
      return `${Math.round((scoredStages / matchStages.length) * 100)}%`;
    } catch { return ''; }
  }

  // Flatten matches into one row per registration (base + extra divisions).
  const entries = [];
  matches.forEach((match, matchIdx) => {
    const regs = myRegistrations(match, matchIdx);
    if (regs.length === 0) {
      entries.push({ match, matchIdx, reg: null });
    } else {
      regs.forEach((reg) => entries.push({ match, matchIdx, reg }));
    }
  });

  function exportCsv() {
    const rows = [['Date', 'Match', 'Shooter ID', 'Shooter Name', 'Division', 'Stages Scored %', 'Result']];
    const csvSafe = (v) => `"${String(v).replace(/"/g, '""')}"`;
    entries.forEach(({ match, matchIdx, reg }) => {
      if (!reg) return;
      const date = `${match.startDate.year}-${String(match.startDate.month).padStart(2, '0')}-${String(match.startDate.day).padStart(2, '0')}`;
      const res = computeResult(match, matchIdx, reg);
      let stagesPct = '';
      let result = '';
      if (res?.dq) {
        result = 'DQ';
      } else if (res) {
        stagesPct = computeStagesPct(match, matchIdx, reg);
        result = `${res.percentage}%`;
      }
      rows.push([date, csvSafe(match.title), reg.publicId, csvSafe(reg.name || ''), reg.division || '', stagesPct, result]);
    });
    const bom = '\uFEFF';
    const csv = bom + rows.map((r) => r.join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `matches-${publicId.replace(/\|/g, '-')}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }

  return (
    <Box sx={{ pb: '80px', bgcolor: '#f5f5f5', minHeight: '100vh' }}>
      {/* Hero header */}
      <Box sx={{
        background: 'linear-gradient(135deg, #1976d2 0%, #1565c0 50%, #0d47a1 100%)',
        color: 'white',
        px: 2, pt: 2, pb: 3,
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography variant="h5" sx={{ fontWeight: 700 }}>
            {t('app.me')}
          </Typography>
          <Box>
            <IconButton onClick={exportCsv} sx={{ color: 'rgba(255,255,255,0.7)' }}>
              <DownloadIcon />
            </IconButton>
            <IconButton onClick={refresh} sx={{ color: 'rgba(255,255,255,0.7)' }}>
              <RefreshIcon />
            </IconButton>
            <IconButton onClick={onLogout} sx={{ color: 'rgba(255,255,255,0.7)' }}>
              <ExitToAppIcon />
            </IconButton>
          </Box>
        </Box>
        <Box sx={{ display: 'flex', gap: 1.5, mt: 1 }}>
          <Box sx={{
            bgcolor: 'rgba(255,255,255,0.15)', borderRadius: 2, px: 1.5, py: 0.5,
            backdropFilter: 'blur(4px)',
          }}>
            <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.7)', display: 'block', lineHeight: 1 }}>
              {t('app.meMatches')}
            </Typography>
            <Typography variant="subtitle2" sx={{ color: 'white', fontWeight: 700 }}>
              {matches.length}
            </Typography>
          </Box>
          <Box sx={{
            bgcolor: 'rgba(255,255,255,0.15)', borderRadius: 2, px: 1.5, py: 0.5,
            backdropFilter: 'blur(4px)',
          }}>
            <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.7)', display: 'block', lineHeight: 1 }}>
              ID
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
              {(() => {
                const cc = publicId.split('|')[0];
                const flagSrc = cc ? getFlagSrc({ countryCode: cc, size: 16 }) : null;
                return flagSrc ? <img src={flagSrc} alt={cc} style={{ width: 16, height: 12 }} /> : null;
              })()}
              <Typography variant="subtitle2" sx={{ color: 'white', fontWeight: 700 }}>
                {publicId.split('|').slice(1).join('|') || publicId}
              </Typography>
            </Box>
          </Box>
        </Box>
      </Box>

      {/* Match list */}
      <Box sx={{ px: 1.5, mt: -1.5, display: 'flex', flexDirection: 'column', gap: 1 }}>
        {entries.length === 0 ? (
          <Paper sx={{ textAlign: 'center', py: 5, borderRadius: 2.5 }}>
            <Typography color="text.secondary">{t('app.meNoMatches')}</Typography>
          </Paper>
        ) : entries.map(({ match, matchIdx, reg }) => {
          const date = moment([match.startDate.year, match.startDate.month - 1, match.startDate.day]).locale(i18n.language).format('LL');
          const staffEntry = staff.find((s) => s.matchId === match.id);
          const staffRoleLabel = staffEntry ? (ROLE_LABELS[staffEntry.role] || staffEntry.role?.toUpperCase() || 'Staff') : null;

          const position = computeResult(match, matchIdx, reg);
          const trophyColor = position && !position.dq ? getTrophyColor(position.pos) : null;
          const categoryTrophyColor = position && !position.dq && position.category ? getTrophyColor(position.categoryPos) : null;
          const accentColor = trophyColor || categoryTrophyColor;

          return (
            <Paper key={`${match.id}:${reg?.id || 'none'}`} sx={{ borderRadius: 2.5, overflow: 'hidden' }}>
              <ButtonBase
                onClick={() => navigate('/app/me/' + match.id)}
                sx={{ p: 0, textAlign: 'start', width: '100%', display: 'flex', alignItems: 'center' }}
              >
                {/* Accent */}
                <Box sx={{
                  width: 4, alignSelf: 'stretch',
                  background: position?.dq
                    ? '#d32f2f'
                    : accentColor
                      ? `linear-gradient(to bottom, ${accentColor}, ${accentColor})`
                      : 'linear-gradient(to bottom, #1976d2, #0d47a1)',
                }} />
                <Box sx={{ flex: 1, p: 1.5 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                    {match.title}
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1, mt: 0.5, alignItems: 'center', flexWrap: 'wrap' }}>
                    <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                      {date}
                    </Typography>
                    {reg && (
                      <>
                        <Box sx={{ width: 3, height: 3, borderRadius: '50%', bgcolor: grey[400] }} />
                        <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                          {[
                            reg.division && t(`divisions.${toDivisionKey(reg.division)}`, { defaultValue: reg.division }),
                            reg.category && t(`categories.${toCategoryKey(reg.category)}`, { defaultValue: reg.category }),
                          ].filter(Boolean).join(' / ')}
                        </Typography>
                      </>
                    )}
                    {staffRoleLabel && (
                      <>
                        <Box sx={{ width: 3, height: 3, borderRadius: '50%', bgcolor: grey[400] }} />
                        <Box sx={{
                          bgcolor: grey[200], borderRadius: 1, px: 0.75, py: 0.125,
                          fontSize: 11, fontWeight: 700, color: 'text.secondary',
                        }}>
                          {staffRoleLabel}
                        </Box>
                      </>
                    )}
                  </Box>

                  {position && position.dq && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
                      <Box sx={{
                        bgcolor: 'error.main', color: 'white',
                        borderRadius: 1, px: 1, py: 0.125,
                        fontSize: 12, fontWeight: 700,
                      }}>
                        DQ
                      </Box>
                    </Box>
                  )}
                  {position && !position.dq && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
                      {trophyColor && <EmojiEventsIcon sx={{ fontSize: 18, color: trophyColor }} />}
                      <Box sx={{
                        bgcolor: 'primary.main', color: 'white',
                        borderRadius: 1, px: 1, py: 0.125,
                        fontSize: 12, fontWeight: 700,
                      }}>
                        {`\u2066#${position.pos} / ${position.total}\u2069`}
                      </Box>
                      <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                        {t('app.meResultPercentage', { percentage: position.percentage })}
                      </Typography>
                    </Box>
                  )}
                  {categoryTrophyColor && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.75 }}>
                      <EmojiEventsIcon sx={{ fontSize: 18, color: categoryTrophyColor }} />
                      <Box sx={{
                        bgcolor: grey[200], color: 'text.primary',
                        borderRadius: 1, px: 1, py: 0.125,
                        fontSize: 12, fontWeight: 700,
                      }}>
                        {`\u2066#${position.categoryPos} / ${position.categoryTotal}\u2069`}
                      </Box>
                      <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                        {t(`categories.${toCategoryKey(position.category)}`, { defaultValue: position.category })}
                      </Typography>
                    </Box>
                  )}
                </Box>
                <Chevron sx={{ color: grey[400], mr: 1 }} />
              </ButtonBase>
            </Paper>
          );
        })}
      </Box>
    </Box>
  );
}
